import { forwardRef, type Ref, type ReactNode } from 'react';
import type { BaseProps } from '../../theme/index.js';
import { useBaseAttrs, splitBaseProps } from '../_internal/factory.js';
import './Progress.css';

export interface ProgressProps extends BaseProps {
  value: number;
  max?: number;
  /** Stripes / animated stripes (Bootstrap style). */
  striped?: boolean;
  animated?: boolean;
  label?: ReactNode;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | number;
}

const SIZE_PX = { xs: 4, sm: 6, md: 8, lg: 12, xl: 16 } as const;

function heightOf(s: ProgressProps['size']): number {
  if (s === undefined) return SIZE_PX.md;
  if (typeof s === 'number') return s;
  return SIZE_PX[s as keyof typeof SIZE_PX] ?? SIZE_PX.md;
}

/** Linear progress bar. */
export const Progress = forwardRef(function Progress(
  { value, max = 100, striped, animated, label, size = 'md', ...rest }: ProgressProps,
  ref: Ref<HTMLDivElement>
): ReactNode {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  const base = useBaseAttrs('rr-progress', rest, [
    striped && 'rr-progress--striped',
    animated && 'rr-progress--animated'
  ]);
  const { dom } = splitBaseProps(rest);
  return (
    <div {...dom}
      ref={ref}
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      {...base.attrs}
      className={base.className}
      style={{ ...base.style, height: heightOf(size) }}
    >
      <div className="rr-progress__bar" style={{ width: `${pct}%` }}>
        {label ? <span className="rr-progress__label">{label}</span> : null}
      </div>
    </div>
  );
});
